import moment from "moment";

/**
 * @param date - The date to format.
 * @param locale - The locale for formatting (default is 'en-US').
 * @returns A formatted date string.
 */
export const formatDate = (date: Date, locale: string = "en-US"): string => {
  return new Intl.DateTimeFormat(locale, {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(date);
};

/**
 * Calculates the difference in days between two dates.
 * @param date1 - The first date.
 * @param date2 - The second date.
 * @returns The difference in days.
 */
export const getDaysDifference = (date1: Date, date2: Date): number => {
  const timeDifference = Math.abs(date2.getTime() - date1.getTime());
  return Math.ceil(timeDifference / (1000 * 60 * 60 * 24));
};

/**
 * Checks if a given date is in the past.
 * @param date - The date to check.
 * @returns True if the date is in the past, otherwise false.
 */
export const isPastDate = (date: Date): boolean => {
  const today = new Date();
  return date < today;
};

/**
 * Adds a specified number of days to a date.
 * @param date - The original date.
 * @param days - The number of days to add.
 * @returns A new date with the added days.
 */
export const addDaysToDate = (date: Date, days: number): Date => {
  const newDate = new Date(date);
  newDate.setDate(newDate.getDate() + days);
  return newDate;
};

/**
 * Converts a date string to a Date object.
 * @param dateString - The date string to convert.
 * @returns A Date object or null if the input is invalid.
 */
export const parseDate = (dateString: string): Date | null => {
  const date = new Date(dateString);
  return isNaN(date.getTime()) ? null : date;
};

/**
 * Converts a date and time string into an formatted string (YYYY-MM-DDTHH:mm:ss).
 * Ensures the date is properly parsed and formatted before processing.
 *
 * @param date - The date input, which can be a string or Date object.
 * @param time - The time string in "HH:mm" format.
 * @returns A formatted date-time string (YYYY-MM-DDTHH:mm:ss) or null if inputs are invalid.
 */
export const formatDateTime = (
  date: string | Date,
  time?: string
): string | null => {
  if (!date) {
    console.error("Invalid input: date is required.");
    return null;
  }

  if (!time) {
    const formattedDate_ = moment(date).format("YYYY-MM-DD");
    const formattedTime = moment().format("HH:mm");
    return `${formattedDate_}T${formattedTime}:00`;
  }

  // Format date as "YYYY-MM-DD"
  const formattedDate = moment(date).format("YYYY-MM-DD");

  // Construct the final date-time string
  return `${formattedDate}T${time}:00`;
};

// Converts a date and time string into an formatted string (MM/DD/YYYY HH:mm).
export const dateTimeFormatted = (isoString: string): string => {
  const date = new Date(isoString);
  const formattedDate = date.toLocaleDateString("en-US");
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });

  return `${formattedDate} ${formattedTime}`;
};

export const formatAppointmentDateTime = (isoString: string): string => {
  if (!isoString) return ""; // ⛔ Prevents 1970 date
  const date = new Date(isoString);
  const formattedDate = date.toLocaleDateString("en-US"); // MM/DD/YYYY
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true, // 12-hour format
  });

  return `${formattedDate} ${formattedTime}`;
};
export function formatAppointmentsDateTime(date: string | Date): string {
  const d = typeof date === "string" ? new Date(date) : date;
  // your formatting logic...
  return d.toLocaleString(); // example
}

// convert 24-hour "HH:mm" string to 12-hour "hh:mm AM/PM"
export const to12Hour = (timeStr: string): string => {
  const [h, m] = timeStr.split(":");
  return new Date(1970, 0, 1, +h, +m)
    .toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    })
    .replace("am", "AM")
    .replace("pm", "PM");
};

// replace 24-hour time range in a string with 12-hour format
export const formatAppointmentInfo = (appointmentInfo: string): string => {
  if (!appointmentInfo) return "";

  const match = appointmentInfo.match(/\d{2}:\d{2}\s-\s\d{2}:\d{2}/);
  if (!match) return appointmentInfo;

  const [start, end] = match[0].split(" - ");
  const convertedRange = `${to12Hour(start)} - ${to12Hour(end)}`;

  return appointmentInfo.replace(match[0], convertedRange);
};

export const parseDateNew = (dateInput: any) => {
  if (!dateInput) return null;

  try {
    let parsedMoment: moment.Moment;

    if (typeof dateInput === "string") {
      if (dateInput.includes("T")) {
        parsedMoment = moment(dateInput); // ISO format
      } else if (dateInput.includes("/")) {
        parsedMoment = moment(dateInput, "MM/DD/YYYY"); // MM/DD/YYYY format
      } else {
        parsedMoment = moment(dateInput); // Auto-detect other formats
      }
    } else if (dateInput instanceof Date) {
      parsedMoment = moment(dateInput);
    } else {
      parsedMoment = moment(dateInput);
    }

    if (parsedMoment.isValid()) {
      return {
        day: parsedMoment.date(),
        month: parsedMoment.format("MMMM"),
        monthIndex: parsedMoment.month(),
        year: parsedMoment.year(),
        date: parsedMoment.toDate(),
        moment: parsedMoment,
      };
    }

    console.error("Invalid date format:", dateInput);
    return null;
  } catch (error) {
    console.error("Error parsing date:", dateInput, error);
    return null;
  }
};

export const getInitialDateValues = (appointmentDate?: any) => {
  const parsedDate = parseDateNew(appointmentDate);

  if (parsedDate) {
    return {
      month: parsedDate.month,
      year: parsedDate.year,
      date: parsedDate.date,
      day: parsedDate.day,
    };
  }

  // Fallback to current date
  const currentDate = new Date();
  return {
    month: currentDate.toLocaleString("default", { month: "long" }),
    year: currentDate.getFullYear(),
    date: currentDate,
  };
};

export const formattedDate = (isoString: string): string => {
  if (!isoString) return "";
  const date = new Date(isoString);
  const formattedDate = date.toLocaleDateString("en-US"); // MM/DD/YYYY
  const formattedTime = date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true, // 12-hour format
  });

  return `${formattedDate}`;
};

/**
 * Convert various date inputs to YYYY-MM format string.
 * @param input - Date object, string (YYYY-MM), timestamp, or null/undefined (current month)
 * @returns Date in YYYY-MM format (local timezone)
 */
export const toYearMonth = (
  input?: Date | string | number | null
): string => {
  let date: Date;

  if (!input) {
    date = new Date();
  } else if (typeof input === "string" && /^\d{4}-\d{2}$/.test(input)) {
    return input;
  } else if (input instanceof Date) {
    date = input;
  } else {
    const parsed = new Date(input);
    date = isNaN(parsed.getTime()) ? new Date() : parsed;
  }

  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");

  return `${year}-${month}`;
};

